/**
 * Session Management Utility
 *
 * Provides persistent anonymous session tracking using localStorage.
 * This allows users to return to a booking in progress without being
 * blocked by their own hold.
 *
 * Key features:
 * - UUID v4 generation for unique session identification
 * - localStorage persistence (survives browser close/reopen)
 * - Fallback to memory for SSR and private browsing
 * - 30-day expiration to prevent stale sessions
 *
 * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/
 */

const SESSION_KEY = 'apex_session_id';
const SESSION_CREATED_KEY = 'apex_session_created';
const SESSION_TTL_DAYS = 30;

// In-memory fallback for SSR or when localStorage is unavailable
let memorySessionId: string | null = null;

/**
 * Generate a UUID v4
 * Uses crypto.randomUUID if available, otherwise falls back to manual generation
 */
function generateUUID(): string {
  // Use native crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const test = '__storage_test__';
    window.localStorage.setItem(test, test);
    window.localStorage.removeItem(test);
    return true;
  } catch {
    // Private browsing mode or storage quota exceeded
    return false;
  }
}

/**
 * Check if session has expired (older than TTL)
 */
function isSessionExpired(): boolean {
  if (!isLocalStorageAvailable()) return false;

  const createdAt = localStorage.getItem(SESSION_CREATED_KEY);
  if (!createdAt) return true;

  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);

  return diffDays > SESSION_TTL_DAYS;
}

/**
 * Get or create a persistent session ID
 *
 * This ID remains constant across page reloads and browser sessions,
 * allowing users to return to their booking without being blocked
 * by their own hold.
 *
 * @returns A unique session identifier (UUID v4 format)
 */
export function getSessionId(): string {
  // Server-side rendering - return memory fallback
  if (typeof window === 'undefined') {
    if (!memorySessionId) {
      memorySessionId = `ssr_${generateUUID()}`;
    }
    return memorySessionId;
  }

  // Try localStorage first
  if (isLocalStorageAvailable()) {
    // Check for existing valid session
    if (!isSessionExpired()) {
      const existingId = localStorage.getItem(SESSION_KEY);
      if (existingId) {
        return existingId;
      }
    }

    // Create new session
    const newId = generateUUID();
    localStorage.setItem(SESSION_KEY, newId);
    localStorage.setItem(SESSION_CREATED_KEY, new Date().toISOString());
    return newId;
  }

  // Fallback to memory (private browsing, etc.)
  if (!memorySessionId) {
    memorySessionId = generateUUID();
  }
  return memorySessionId;
}

/**
 * Clear the current session
 * Useful for logout or when user wants to start fresh
 */
export function clearSession(): void {
  memorySessionId = null;

  if (isLocalStorageAvailable()) {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_CREATED_KEY);
  }
}

/**
 * Refresh the session timestamp
 * Extends the TTL without changing the session ID
 */
export function refreshSession(): void {
  if (isLocalStorageAvailable() && localStorage.getItem(SESSION_KEY)) {
    localStorage.setItem(SESSION_CREATED_KEY, new Date().toISOString());
  }
}

/**
 * Get session metadata (for debugging)
 */
export function getSessionInfo(): {
  id: string;
  createdAt: string | null;
  expiresAt: string | null;
  storage: 'localStorage' | 'memory';
} {
  const id = getSessionId();

  if (isLocalStorageAvailable()) {
    const createdAt = localStorage.getItem(SESSION_CREATED_KEY);
    let expiresAt: string | null = null;

    if (createdAt) {
      const expires = new Date(createdAt);
      expires.setDate(expires.getDate() + SESSION_TTL_DAYS);
      expiresAt = expires.toISOString();
    }

    return {
      id,
      createdAt,
      expiresAt,
      storage: 'localStorage',
    };
  }

  return {
    id,
    createdAt: null,
    expiresAt: null,
    storage: 'memory',
  };
}
